import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import DateRangePicker from '../../components/DateRangePicker';
import inventoryAPI from '../../api/inventoryAPI';
import { formatDate } from '../../utils/dateUtils';
import { exportToCSV, formatInventoryForExport } from '../../utils/csvExport';

const InventoryList = () => {
    const [inventoryData, setInventoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const navigate = useNavigate();

    useEffect(() => {
        fetchInventoryData();
    }, [dateRange]);

    useEffect(() => {
        filterInventoryData();
    }, [searchTerm, inventoryData]);

    const fetchInventoryData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await inventoryAPI.getAll({
                populate: 'product,store',
                includeTransactions: true,
                startDate: dateRange.startDate?.toISOString(),
                endDate: dateRange.endDate?.toISOString()
            });
        
            const transformedData = response.map(item => {
                const transactions = item.transactions || [];
                
                // SIMPLE CALCULATION: Just sum up all quantity changes
                let totalStockIn = 0;
                let totalStockOut = 0;
                
                transactions.forEach(tx => {
                    if (tx.quantityChange > 0) {
                        totalStockIn += tx.quantityChange;
                    } else {
                        totalStockOut += Math.abs(tx.quantityChange);
                    }
                });

                // For debugging Aguha
                if (item.product?.name === 'Aguha' || item.productName === 'Aguha') {
                    console.log('=== AGUHA DEBUG ===');
                    console.log('API quantityOnHand:', item.quantityOnHand);
                    console.log('Transactions:', transactions);
                    console.log('Stock In:', totalStockIn);
                    console.log('Stock Out:', totalStockOut);
                    console.log('Expected Balance (0 + 20 - 5):', 0 + totalStockIn - totalStockOut);
                    console.log('Actual Balance from API:', item.quantityOnHand);
                    console.log('==================');
                }

                // Use the API's quantityOnHand as the final balance
                // Beginning balance = current balance - net changes
                const beginningBalance = item.quantityOnHand - totalStockIn + totalStockOut;

                return {
                    ...item,
                    productName: item.product?.name || 'N/A',
                    barcode: item.product?.barcode || 'N/A',
                    reOrderLevel: item.product?.reOrderLevel || 0,
                    beginningBalance,
                    stockIn: totalStockIn,
                    stockOut: totalStockOut,
                    balance: item.quantityOnHand, // This comes from API
                    allTransactions: transactions
                };
            });
        
            setInventoryData(transformedData);
        } catch (error) {
            setError(error.message || 'Failed to load inventory data');
            setInventoryData([]);
        } finally {
            setLoading(false);
        }
    };

    const filterInventoryData = () => {
        let filtered = [...inventoryData];

        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(item => 
                (item.productName?.toLowerCase() || '').includes(lowercasedSearch) ||
                (item.barcode?.toLowerCase() || '').includes(lowercasedSearch) ||
                (item.store?.name?.toLowerCase() || '').includes(lowercasedSearch)
            );
        }

        if (dateRange.startDate && dateRange.endDate) {
            filtered = filtered.map(item => {
                const filteredTransactions = item.allTransactions?.filter(tx => {
                    const txDate = new Date(tx.createdAt);
                    return txDate >= dateRange.startDate && txDate <= dateRange.endDate;
                }) || [];

                let periodStockIn = 0;
                let periodStockOut = 0;
                
                filteredTransactions.forEach(tx => {
                    if (tx.quantityChange > 0) {
                        periodStockIn += tx.quantityChange;
                    } else {
                        periodStockOut += Math.abs(tx.quantityChange);
                    }
                });

                const transactionsBeforePeriod = item.allTransactions?.filter(tx => {
                    const txDate = new Date(tx.createdAt);
                    return txDate < dateRange.startDate;
                }) || [];

                let balanceBeforePeriod = item.beginningBalance;
                transactionsBeforePeriod.forEach(tx => {
                    balanceBeforePeriod += tx.quantityChange;
                });

                const periodEndBalance = balanceBeforePeriod + periodStockIn - periodStockOut;

                return {
                    ...item,
                    beginningBalance: balanceBeforePeriod,
                    stockIn: periodStockIn,
                    stockOut: periodStockOut,
                    balance: periodEndBalance,
                    filteredTransactions
                };
            });
        }

        setFilteredData(filtered);
    };

    const handleExportCSV = async () => {
        try {
            setExportLoading(true);
            
            const dataToExport = searchTerm || dateRange.startDate ? filteredData : inventoryData;
            
            if (dataToExport.length === 0) {
                alert('No data to export');
                return;
            }

            const formattedData = formatInventoryForExport(dataToExport);
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `inventory-export-${timestamp}.csv`;
            
            exportToCSV(formattedData, filename);
            
        } catch (error) {
            setError('Failed to export data: ' + error.message);
        } finally {
            setExportLoading(false);
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const handleDateRangeChange = (range) => {
        setDateRange(range);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ startDate: null, endDate: null });
    };

    const stockLevelClass = (balance, reOrderLevel) => {
        if (balance <= 0) {
            return 'bg-red-100 text-red-800';
        } else if (balance <= reOrderLevel) {
            return 'bg-yellow-100 text-yellow-800';
        }
        return 'bg-green-100 text-green-800';
    };

    const columns = [
        {
            title: 'Product Name',
            key: 'productName',
            render: (item) => (
                <span className="font-medium">{item.productName}</span>
            )
        },
        {
            title: 'Barcode',
            key: 'barcode',
            render: (item) => item.barcode
        },
        {
            title: 'Beginning Balance',
            key: 'beginningBalance',
            render: (item) => item.beginningBalance.toFixed(2)
        },
        {
            title: 'Stock In',
            key: 'stockIn',
            render: (item) => (
                <span className="text-green-600">{item.stockIn.toFixed(2)}</span>
            )
        },
        {
            title: 'Stock Out',
            key: 'stockOut',
            render: (item) => (
                <span className="text-red-600">{item.stockOut.toFixed(2)}</span>
            )
        },
        {
            title: 'Balance',
            key: 'balance',
            render: (item) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${stockLevelClass(item.balance, item.reOrderLevel)}`}>
                    {item.balance.toFixed(2)}
                </span>
            )
        },
        {
            title: 'Reorder Level',
            key: 'reOrderLevel',
            render: (item) => item.reOrderLevel.toFixed(2)
        }
    ];

    return (
        <Layout pageTitle="Inventory Management">
            <div className="bg-gray-100 mx-auto p-6">
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-4">
                        <DebouncedSearchInput
                            placeholder="Search by product name, barcode or store..."
                            onSearch={handleSearch}
                            className="flex-1 mr-4 max-w-md"
                        />

                        <DateRangePicker 
                            onRangeChange={handleDateRangeChange}
                            initialRange={dateRange}
                        />

                        {(searchTerm || dateRange.startDate) && (
                            <button
                                onClick={handleClearFilters}
                                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>

                    <div className="flex items-center space-x-3">
                        <button 
                            onClick={handleExportCSV}
                            disabled={exportLoading || filteredData.length === 0}
                            className="transition-all duration-300 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {exportLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Export CSV
                                </>
                            )}
                        </button>

                        <button 
                            onClick={() => navigate('/inventory/adjustment')}
                            className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
                        >
                            <span className="mr-1">+</span> Inventory Adjustment
                        </button>
                    </div>
                </div>

                <div className="mb-2">
                    {searchTerm && (
                        <div className="text-sm text-gray-500">
                            Showing {filteredData.length} of {inventoryData.length} products matching "{searchTerm}"
                        </div>
                    )}
                    {dateRange.startDate && dateRange.endDate && (
                        <div className="text-sm text-gray-500">
                            Date range: {formatDate(dateRange.startDate)} to {formatDate(dateRange.endDate)}
                        </div>
                    )}
                </div>

                <DataTable
                    columns={columns}
                    data={filteredData}
                    loading={loading}
                    emptyMessage="No inventory items found"
                    onRowClick={(item) => navigate(`/inventory/${item._id}`)}
                />
            </div>
        </Layout>
    );
};

export default InventoryList;